import React, { useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { FiFileText, FiMessageCircle, FiArrowRight, FiEdit2, FiMail } from 'react-icons/fi';
import ShieldCheckIcon from '../ShieldCheckIcon';
import { SettingsHeader } from './ClientSettings';
import { INVOICES, BILLING, BillingDetails, money, invoiceTotal, useClient } from './clientSettingsData';
import ChangePlan from './ChangePlan';
import CancelSubscriptionModal from './CancelSubscriptionModal';
import UpdateCardModal from './UpdateCardModal';
import CardBrandLogo, { CardBrand } from './CardBrand';
import EditBillingModal from './EditBillingModal';

// Investi bills once a year; plans and cancellations are requested, only the card and billing contact are edited here.
// /client-settings/billing/plans is the plan list (Request a Plan Change)
const Billing: React.FC = () => {
    const { section } = useParams<{ section?: string }>();
    if (section === 'plans') return <ChangePlan />;
    return <BillingOverview />;
};

const BillingOverview: React.FC = () => {
    const history = useHistory();
    const { stock, name } = useClient();
    const [details, setDetails] = useState<BillingDetails>(BILLING);
    const [card, setCard] = useState<{ brand: CardBrand; last4: string; expiry: string; holder: string }>({ brand: 'visa', last4: '3467', expiry: '01/2030', holder: name });
    const [cancelling, setCancelling] = useState(false);
    const [updatingCard, setUpdatingCard] = useState(false);
    const [editingDetails, setEditingDetails] = useState(false);

    const address = `${details.address}, ${details.suburb} ${details.state} ${details.postcode}`;

    return <>
        <SettingsHeader title="Billing" sub="Each client company has its own plan and its own card. Investi invoices this company once a year." />

        <div className="banner">
            <span className="icon-tile"><ShieldCheckIcon /></span>
            <div>
                <div className="title">Plans are changed by Investi</div>
                <div className="hint">To move this client up or down a plan, or to cancel, send a request and your Investi account manager will confirm it the same day. The card on file can be updated here by the account owner.</div>
            </div>
        </div>

        <div className="cs-row">
            <div className="cs-main">
                <section className="card">
                    <div className="section-title">
                        <span className="icon-tile"><FiFileText /></span>
                        <div className="text">
                            <h2>Current plan</h2>
                            <div className="hint">Billed once a year to {name} · renews 1 September 2027</div>
                        </div>
                        <span className="badge ok">Active</span>
                    </div>

                    <div className="price">
                        <div>
                            <strong>Growth</strong>
                            <span>The full platform — registry, communications and Website Hub</span>
                        </div>
                        <div className="amount">
                            <strong>A$8,900</strong>
                            <span>per year + GST</span>
                            <em>12 months for the price of 10</em>
                        </div>
                    </div>

                    <div className="plan-actions">
                        <button type="button" className="btn-outline with-icon" onClick={() => history.push('/client-settings/billing/plans')}><FiMessageCircle /> Request a Plan Change</button>
                        <button type="button" className="quiet" onClick={() => setCancelling(true)}>Request Cancellation</button>
                        <span className="renews">Renews 1 Sep 2027</span>
                    </div>
                </section>

                <section className="card">
                    <div className="section-title">
                        <span className="icon-tile"><FiFileText /></span>
                        <div className="text">
                            <h2>Invoices</h2>
                            <div className="hint">Emailed to the billing contact below when the subscription renews</div>
                        </div>
                    </div>
                    <div className="invoices">
                        <div className="tr th">
                            <span className="inv">Invoice</span>
                            <span className="date">Date</span>
                            <span className="amt">Amount</span>
                            <span className="status">Status</span>
                            <span className="pdf" />
                        </div>
                        {INVOICES.map(i => <div key={i.id} className="tr">
                            <span className="inv"><strong>{i.id}</strong></span>
                            <span className="date">{i.date}</span>
                            <span className="amt">{money(invoiceTotal(i))}</span>
                            <span className="status"><span className="badge ok">Paid</span></span>
                            <span className="pdf">
                                <button type="button" className="link" onClick={() => window.open(`/invoice/${stock?.ticker}:${stock?.exchange}/${i.key}`, '_blank')}>PDF <FiArrowRight /></button>
                            </span>
                        </div>)}
                    </div>
                </section>
            </div>

            <div className="cs-side">
                <section className="card">
                    <div className="section-title">
                        <span className="icon-tile"><ShieldCheckIcon /></span>
                        <div className="text">
                            <h2>Card on file</h2>
                            <div className="hint">Charged once a year on the renewal date</div>
                        </div>
                    </div>
                    <div className="credit-card">
                        <div className="top"><span className="chip" /><CardBrandLogo brand={card.brand} onDark /></div>
                        <div className="number">•••• •••• •••• {card.last4}</div>
                        <div className="bottom">
                            <div><small>CARDHOLDER</small><span>{card.holder}</span></div>
                            <div className="right"><small>EXPIRES</small><span>{card.expiry}</span></div>
                        </div>
                    </div>
                    <button type="button" className="btn-primary full" onClick={() => setUpdatingCard(true)}><FiEdit2 /> Update Card Details</button>
                    <div className="permission"><ShieldCheckIcon /> Only the account owner can change the card. You are signed in as the account owner for {name}.</div>
                </section>

                <section className="card">
                    <div className="section-title">
                        <span className="icon-tile"><FiMail /></span>
                        <div className="text">
                            <h2>Billing contact</h2>
                            <div className="hint">Where invoices and receipts are emailed</div>
                        </div>
                    </div>
                    <div className="contact">
                        <div><span>Name</span><strong>{details.contact}</strong></div>
                        <div><span>Email</span><strong>{details.email}</strong></div>
                        <div><span>Company</span><strong>{name}</strong></div>
                        <div><span>ABN</span><strong>{details.abn}</strong></div>
                        {details.po && <div><span>PO number</span><strong>{details.po}</strong></div>}
                        <div><span>Address</span><strong>{address}</strong></div>
                    </div>
                    <button type="button" className="btn-outline with-icon" onClick={() => setEditingDetails(true)}><FiEdit2 /> Edit Billing Contact</button>
                </section>
            </div>
        </div>

        {cancelling && <CancelSubscriptionModal onClose={() => setCancelling(false)} />}
        {updatingCard && <UpdateCardModal holder={card.holder} onClose={() => setUpdatingCard(false)}
            onSave={c => { setCard(c); setUpdatingCard(false); }} />}
        {editingDetails && <EditBillingModal details={details} onClose={() => setEditingDetails(false)}
            onSave={d => { setDetails(d); setEditingDetails(false); }} />}
    </>;
};

export default Billing;
